const DerivAPIBasic = require('@deriv/deriv-api/dist/DerivAPIBasic');
const getAppId = require('@deriv/shared').getAppId;
const isLegacyApiAppId = require('@deriv/shared').isLegacyApiAppId;
const useNewWs = require('@deriv/shared').useNewWs;
const getSocketURL = require('@deriv/shared').getSocketURL;
const cloneObject = require('@deriv/shared').cloneObject;
const getPropertyValue = require('@deriv/shared').getPropertyValue;
const State = require('@deriv/shared').State;
const { getLanguage } = require('@deriv/translations');
const website_name = require('@deriv/shared').website_name;
const SocketCache = require('./socket_cache');
const APIMiddleware = require('./api_middleware');

/*
 * An abstraction layer over native javascript WebSocket,
 * which provides additional functionality like
 * reopen the closed connection and process the buffered requests
 */
const BinarySocketBase = (() => {
    let deriv_api, binary_socket, client_store;

    let config = {};
    let wrong_app_id = 0;
    let is_disconnect_called = false;
    let is_connected_before = false;
    let is_switching_socket = false;

    const availability = {
        is_up: true,
        is_updating: false,
        is_down: false,
    };

    const getSocketUrl = (language, is_mock_server = false) => {
        if (is_mock_server) {
            return 'ws://127.0.0.1:42069';
        }
        return `wss://${getSocketURL()}/websockets/v3?app_id=${getAppId()}&l=${language}&brand=${website_name.toLowerCase()}`;
    };

    const isReady = () => hasReadyState(1);

    const isClose = () => !binary_socket || hasReadyState(2, 3);

    const blockRequest = value => deriv_api?.blockRequest(value);

    const close = () => {
        binary_socket.close();
    };

    const closeAndOpenNewConnection = (language = getLanguage(), session_id = '') => {
        close();
        is_switching_socket = true;
        openNewConnection(language, session_id);
    };

    const hasReadyState = (...states) => binary_socket && states.some(s => binary_socket.readyState === s);

    const init = ({ options, client }) => {
        if (typeof options === 'object' && config !== options) {
            config = options;
        }
        client_store = client;
    };

    const getMockServerConfig = () => {
        const mock_server_config = localStorage.getItem('mock_server_data');
        return mock_server_config
            ? JSON.parse(mock_server_config)
            : {
                  session_id: '',
                  is_mockserver_enabled: false,
              };
    };

    const openNewConnection = (language = getLanguage()) => {
        const mock_server_config = getMockServerConfig();
        const session_id = mock_server_config?.session_id || '';

        if (wrong_app_id === getAppId()) return;

        // Skip when running against a new portal app_id — legacy WS rejects them with
        // 401 at handshake, the close handler immediately reconnects, and the loop
        // hammers Deriv's edge until CF rate-limits us (error 1015). The new WS
        // endpoint family (api.derivws.com/trading/v1/...) is wired up in a separate
        // PR; until then, the app runs in a "logged-in-but-no-live-data" state.
        if (!isLegacyApiAppId()) return;

        if (!is_switching_socket) config.wsEvent('init');

        if (isClose()) {
            is_disconnect_called = false;
            binary_socket = new WebSocket(getSocketUrl(language, session_id));

            deriv_api = new DerivAPIBasic({
                connection: binary_socket,
                storage: SocketCache,
                middleware: new APIMiddleware(config, session_id),
            });
        }

        deriv_api.onOpen().subscribe(() => {
            config.wsEvent('open');

            wait('website_status');

            if (client_store.is_logged_in) {
                const authorize_token = client_store.getToken();
                deriv_api.authorize(authorize_token);
            }

            if (typeof config.onOpen === 'function') {
                config.onOpen(isReady());
            }

            if (typeof config.onReconnect === 'function' && is_connected_before) {
                config.onReconnect();
            }

            if (!is_connected_before) {
                is_connected_before = true;
            }
        });

        deriv_api.onMessage().subscribe(({ data: response }) => {
            const msg_type = response.msg_type;
            State.set(['response', msg_type], cloneObject(response));

            config.wsEvent('message');

            if (getPropertyValue(response, ['error', 'code']) === 'InvalidAppID') {
                wrong_app_id = getAppId();
            }

            if (typeof config.onMessage === 'function') {
                config.onMessage(response);
            }
        });

        deriv_api.onClose().subscribe(() => {
            if (!is_switching_socket) {
                config.wsEvent('close');
            } else {
                is_switching_socket = false;
            }

            if (wrong_app_id !== getAppId() && typeof config.onDisconnect === 'function' && !is_disconnect_called) {
                config.onDisconnect();
                is_disconnect_called = true;
            }
        });
    };

    const isSiteUp = status => /^up$/i.test(status);

    const isSiteUpdating = status => /^updating$/i.test(status);

    const isSiteDown = status => /^down$/i.test(status);

    // if status is up or updating, consider site available
    // if status is down, consider site unavailable
    const setAvailability = status => {
        availability.is_up = isSiteUp(status);
        availability.is_updating = isSiteUpdating(status);
        availability.is_down = isSiteDown(status);
    };

    const excludeAuthorize = type => !(type === 'authorize' && !client_store.is_logged_in);

    // When the legacy WS is intentionally gated (see `isLegacyApiAppId` in
    // `openNewConnection`), `deriv_api` is never initialised. Pre-PR-A.1, this
    // function would block on `expectResponse` until the WS authorized; now it
    // returned `undefined`, and every direct caller doing
    // `WS.wait(...).then(...)` would crash with `Cannot read properties of
    // undefined (reading 'then')`. Returning a never-resolving promise
    // preserves the pre-gate semantic — callers hang silently rather than
    // crash the app into the error boundary.
    // PR-A.1.3: the @deriv/trader (relocated to '/dtrader') awaits
    // `WS.wait('website_status')` / `wait('authorize')` / `wait('get_settings')`
    // as a bootstrap gate BEFORE it requests active_symbols / contracts_for /
    // ticks. On new portal app_ids `deriv_api` is undefined so the legacy
    // branch returns a never-resolving promise (see docblock above) → the
    // trade form never loads.
    //
    // We resolve those waits via the registered resolver ONLY when the trader
    // is the active surface (route is '/dtrader' or the trader's '/contract'
    // deep-link). This is deliberately scoped: on the bot ('/') and every
    // other route, `WS.wait(...)` keeps its EXACT pre-existing never-resolving
    // behaviour, so no core/bot store that relied on those waits staying
    // pending (portfolio / gtm / contract-replay / common) is re-enabled or
    // regressed. The bot never calls WS.wait for its trade path anyway (it
    // uses its own PublicWsApiShim), so this is purely additive for the trader.
    // PR-A.2.3: the NATIVE Manual Trading tab mounts `@deriv/trader` in-process
    // at the BOT route (pathname '/' + hash '#manual_trading'), NOT at
    // '/dtrader'. Its MemoryRouter pins the trader's INTERNAL router to
    // '/dtrader' so the Trade module renders, but the GLOBAL
    // `window.location.pathname` stays '/'. So the pathname check below returns
    // false → `WS.wait()` / the `authorized` proxy fall through to the
    // never-resolving promise → the trade-store hangs with no data (empty
    // chart + skeleton panel — the #294→#295 symptom). The native mount sets
    // `window.__FX_NATIVE_TRADER_MOUNTED__` on mount (cleared on unmount), so
    // the trader-scoped data shims activate exactly while the native trader is
    // on screen and revert to the prior never-resolving behaviour everywhere
    // else. Reading a window flag is consistent with the existing
    // `window.location` read here and decouples core from bot-web-ui.
    // NOTE: flipping the flag does NOT retroactively resolve waits already
    // issued at boot (each `wait()` call's resolution is fixed at call time),
    // so background bot stores that awaited at init are unaffected.
    const isTraderRoute = () => {
        try {
            if (typeof window !== 'undefined' && window.__FX_NATIVE_TRADER_MOUNTED__ === true) {
                return true;
            }
            const p = window?.location?.pathname || '';
            return p === '/dtrader' || p.indexOf('/contract') === 0;
        } catch {
            return false;
        }
    };

    const wait = (...responses) => {
        if (useNewWs() && _new_ws_wait && isTraderRoute()) {
            return _new_ws_wait(...responses.filter(excludeAuthorize));
        }
        return deriv_api?.expectResponse(...responses.filter(excludeAuthorize)) ?? new Promise(() => {});
    };

    const subscribe = (request, cb) => deriv_api.subscribe(request).subscribe(cb, cb); // Delegate error handling to the callback

    const balanceAll = () => deriv_api.send({ balance: 1, account: 'all' });

    const subscribeBalanceAll = cb => subscribe({ balance: 1, account: 'all' }, cb);

    const subscribeBalanceActiveAccount = (cb, account) => subscribe({ balance: 1, account }, cb);

    // PR-B.2.4: deferred registration of new-WS proposal impls. Avoids a
    // circular import between socket_base.js (lower layer) and
    // socket-general.js (higher layer). socket-general.js calls
    // `setProposalNewWsImpl(...)` once after defining its services, and
    // subscribeProposal / forgetAll route through the new impls when
    // `useNewWs()` is true. Legacy app_ids keep the original path
    // byte-identical (registry is null until socket-general initialises).
    let _new_ws_proposal_subscribe = null;
    let _new_ws_proposal_forget_all = null;
    // PR-B.2.5: buy/sell impls are registered alongside proposal because
    // they share a same-socket constraint with proposal — the new auth WS
    // scopes proposal_ids to the WebSocket that issued them, so the
    // subsequent `buy` call must reuse that same socket. socket-general.js
    // satisfies this by routing both through `getAuthSocket()`, which
    // returns a singleton per active account.
    let _new_ws_buy = null;
    let _new_ws_sell = null;
    // PR-B.2.6: proposal_open_contract subscribe + bulk-forget.
    let _new_ws_poc_subscribe = null;
    let _new_ws_poc_forget_all = null;
    // PR-B.2.7: transaction stream + statement one-shot.
    let _new_ws_transaction_subscribe = null;
    let _new_ws_transaction_forget_all = null;
    let _new_ws_statement = null;
    // PR-CU.1: contract_update / contract_update_history / cancel on the auth WS.
    let _new_ws_contract_update = null;
    let _new_ws_contract_update_history = null;
    let _new_ws_cancel_contract = null;
    // PR-A.1.3: PUBLIC-DATA registry. The bot reaches the new public WS via
    // its own `api_base.api` (a PublicWsApiShim instance built on mount by
    // DBot.initApi). But the @deriv/trader package — relocated to the
    // standalone '/dtrader' route — talks to the new portal exclusively
    // through this `WS` facade (`WS.activeSymbols`, `WS.contractsFor`,
    // `WS.subscribeTicksHistory`, `WS.tradingTimes`, `WS.time`, `WS.wait`).
    // The bot does NOT mount at '/dtrader', so its shim never initialises and
    // these methods fall through to the DEAD legacy `deriv_api` (gated off for
    // new portal app_ids in `openNewConnection`). The result: the trade form
    // + chart stay blank because no WS ever connects.
    //
    // This registry — mirroring the PR-B.2.x proposal registry above — lets
    // socket-general.js install new-public-WS impls (built on the SAME
    // `getPublicSocket()` singleton the shim uses, so no extra connection) so
    // the trader's public-data calls resolve on app_id 33bpLHh7yQEF…
    // regardless of route. Legacy numeric app_ids leave the registry null and
    // keep the original `deriv_api` path byte-identically.
    let _new_ws_active_symbols = null;
    let _new_ws_contracts_for = null;
    let _new_ws_trading_times = null;
    let _new_ws_ticks_history_send = null;
    let _new_ws_ticks_history_subscribe = null;
    let _new_ws_ticks_subscribe = null;
    let _new_ws_time = null;
    let _new_ws_public_send = null;
    let _new_ws_forget = null;
    // `wait(type)` resolver. The trader's prepareTradeStore awaits
    // `WS.wait('website_status')` and `WS.authorized.X` awaits
    // `wait('authorize')` before issuing data calls. On new portal app_ids
    // `deriv_api` is undefined so the legacy `wait` returns a never-resolving
    // promise → the trader hangs forever before it ever requests data. The
    // resolver below (registered by socket-general) returns an
    // already-resolved synthetic envelope for these bootstrap waits so the
    // trader proceeds to the real (now-routed) data calls.
    let _new_ws_wait = null;
    const setPublicDataNewWsImpl = ({
        activeSymbols: activeSymbolsImpl,
        contractsFor: contractsForImpl,
        tradingTimes: tradingTimesImpl,
        getTicksHistory: ticksHistorySendImpl,
        subscribeTicksHistory: ticksHistorySubscribeImpl,
        subscribeTicks: ticksSubscribeImpl,
        time: timeImpl,
        send: publicSendImpl,
        forget: forgetImpl,
        wait: waitImpl,
    } = {}) => {
        _new_ws_active_symbols = typeof activeSymbolsImpl === 'function' ? activeSymbolsImpl : null;
        _new_ws_contracts_for = typeof contractsForImpl === 'function' ? contractsForImpl : null;
        _new_ws_trading_times = typeof tradingTimesImpl === 'function' ? tradingTimesImpl : null;
        _new_ws_ticks_history_send = typeof ticksHistorySendImpl === 'function' ? ticksHistorySendImpl : null;
        _new_ws_ticks_history_subscribe =
            typeof ticksHistorySubscribeImpl === 'function' ? ticksHistorySubscribeImpl : null;
        _new_ws_ticks_subscribe = typeof ticksSubscribeImpl === 'function' ? ticksSubscribeImpl : null;
        _new_ws_time = typeof timeImpl === 'function' ? timeImpl : null;
        _new_ws_public_send = typeof publicSendImpl === 'function' ? publicSendImpl : null;
        _new_ws_forget = typeof forgetImpl === 'function' ? forgetImpl : null;
        _new_ws_wait = typeof waitImpl === 'function' ? waitImpl : null;
    };
    const setProposalNewWsImpl = ({
        subscribeProposal: subImpl,
        forgetAllProposals: forgetImpl,
        buy: buyImpl,
        sell: sellImpl,
        subscribeProposalOpenContract: pocSubImpl,
        forgetAllProposalOpenContract: pocForgetImpl,
        subscribeTransaction: txSubImpl,
        forgetAllTransaction: txForgetImpl,
        statement: statementImpl,
        contractUpdate: contractUpdateImpl,
        contractUpdateHistory: contractUpdateHistoryImpl,
        cancelContract: cancelContractImpl,
    } = {}) => {
        _new_ws_proposal_subscribe = typeof subImpl === 'function' ? subImpl : null;
        _new_ws_proposal_forget_all = typeof forgetImpl === 'function' ? forgetImpl : null;
        _new_ws_buy = typeof buyImpl === 'function' ? buyImpl : null;
        _new_ws_sell = typeof sellImpl === 'function' ? sellImpl : null;
        _new_ws_poc_subscribe = typeof pocSubImpl === 'function' ? pocSubImpl : null;
        _new_ws_poc_forget_all = typeof pocForgetImpl === 'function' ? pocForgetImpl : null;
        _new_ws_transaction_subscribe = typeof txSubImpl === 'function' ? txSubImpl : null;
        _new_ws_transaction_forget_all = typeof txForgetImpl === 'function' ? txForgetImpl : null;
        _new_ws_statement = typeof statementImpl === 'function' ? statementImpl : null;
        _new_ws_contract_update = typeof contractUpdateImpl === 'function' ? contractUpdateImpl : null;
        _new_ws_contract_update_history =
            typeof contractUpdateHistoryImpl === 'function' ? contractUpdateHistoryImpl : null;
        _new_ws_cancel_contract = typeof cancelContractImpl === 'function' ? cancelContractImpl : null;
    };

    const subscribeProposal = (req, cb) => {
        if (useNewWs() && _new_ws_proposal_subscribe) {
            // Service handles request-schema translation
            // (`symbol` → `underlying_symbol`) at the boundary and returns
            // `undefined`. We mimic the legacy contract here: callers receive
            // proposal messages via `cb`, not via a return value.
            _new_ws_proposal_subscribe(req, cb);
            // Return a minimal stub for callers that .unsubscribe() the
            // returned handle. Service tracks subscriptions internally and
            // tears them down via forgetAllProposals().
            return { unsubscribe: () => undefined };
        }
        return subscribe({ proposal: 1, ...req }, cb);
    };

    // Explicit `forgetAll` override. Without this, WS.forgetAll(...) would
    // fall through delegateToObject → deriv_api.forgetAll which only knows
    // about the LEGACY socket's stream subscriptions. When `useNewWs()` is
    // true and the stream type is `proposal`, we additionally tear down the
    // new-WS proposal subscriptions tracked inside socket-general.
    const forgetAll = (...types) => {
        if (useNewWs()) {
            if (_new_ws_proposal_forget_all && types.includes('proposal')) {
                try { _new_ws_proposal_forget_all(); } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('[socket_base.forgetAll] new-WS proposal forget failed:', e?.message ?? e);
                }
            }
            // PR-B.2.6: same teardown path for proposal_open_contract streams.
            if (_new_ws_poc_forget_all && types.includes('proposal_open_contract')) {
                try { _new_ws_poc_forget_all(); } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('[socket_base.forgetAll] new-WS POC forget failed:', e?.message ?? e);
                }
            }
            // PR-B.2.7: same teardown path for the transaction stream.
            if (_new_ws_transaction_forget_all && types.includes('transaction')) {
                try { _new_ws_transaction_forget_all(); } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('[socket_base.forgetAll] new-WS transaction forget failed:', e?.message ?? e);
                }
            }
        }
        // Always also call legacy deriv_api.forgetAll so any legacy streams
        // (or future stream types) still get cleaned up. No-op on new app_ids
        // where deriv_api is undefined.
        return deriv_api?.forgetAll?.(...types);
    };

    const subscribeProposalOpenContract = (contract_id = null, cb, consumer_key) => {
        // PR-B.2.6: route through the new-WS impl when active. POC schema is
        // byte-identical (probe-verified 2026-05-12): request shape
        // `{proposal_open_contract: 1, subscribe: 1, contract_id?}` returns
        // `{proposal_open_contract: {<35-field contract state>}, msg_type,
        // subscription: {id}}`. Same single-socket requirement as proposal —
        // satisfied by getAuthSocket() singleton.
        // PR-POC.2: the new-WS impl fans ONE shared subscription per POC key out to
        // many consumers keyed by `consumer_key` ('bot', 'portfolio', 'replay', …)
        // and returns a real per-consumer handle — forward the key + handle so a
        // consumer's subscribe/teardown no longer starves the others (a running bot
        // sharing the `__all__` stream with the trader).
        if (useNewWs() && _new_ws_poc_subscribe) {
            return _new_ws_poc_subscribe(contract_id, cb, consumer_key);
        }
        return subscribe({ proposal_open_contract: 1, ...(contract_id && { contract_id }) }, cb);
    };

    const subscribeTicks = (symbol, cb) => {
        // PR-A.1.3: route the trader's tick stream to the new public WS.
        if (useNewWs() && _new_ws_ticks_subscribe) {
            return _new_ws_ticks_subscribe(symbol, cb);
        }
        return subscribe({ ticks: symbol }, cb);
    };

    const subscribeTicksHistory = (request_object, cb) => {
        // PR-A.1.3: the trader's chart subscribes via ticks_history (with
        // `subscribe: 1`). Route to the new public WS so candles + the live
        // tick stream populate the chart at '/dtrader'.
        if (useNewWs() && _new_ws_ticks_history_subscribe) {
            return _new_ws_ticks_history_subscribe(request_object, cb);
        }
        return subscribe(request_object, cb);
    };

    const subscribeTransaction = (cb, key) => {
        // PR-B.2.7: route through the new-WS impl when active. Schema is
        // byte-identical (probe-verified): request `{transaction: 1,
        // subscribe: 1}` returns a stream of `{transaction: {id, ...},
        // msg_type: 'transaction', subscription: {id}}` messages with each
        // buy/sell/payout event.
        // PR-TXN.2: the new-WS impl now fans ONE shared subscription out to many
        // consumers keyed by `key` ('portfolio', 'bot', …) and returns a real
        // per-consumer unsubscribe handle — forward the key and the handle so a
        // consumer's teardown no longer starves the others.
        if (useNewWs() && _new_ws_transaction_subscribe) {
            return _new_ws_transaction_subscribe(cb, key);
        }
        return subscribe({ transaction: 1 }, cb);
    };

    const subscribeWebsiteStatus = cb => subscribe({ website_status: 1 }, cb);

    const getTicksHistory = request_object => {
        // PR-A.1.3: the trader's market-closed branch fetches candles one-shot
        // via getTicksHistory. Route to the new public WS.
        if (useNewWs() && _new_ws_ticks_history_send) {
            return _new_ws_ticks_history_send(request_object);
        }
        return deriv_api.send(request_object);
    };

    const buyAndSubscribe = request => {
        return new Promise(resolve => {
            let called = false;
            const subscriber = subscribe(request, response => {
                if (!called) {
                    called = true;
                    subscriber.unsubscribe();
                    resolve(response);
                }
            });
        });
    };

    const buy = ({ proposal_id, price, parameters, subscribe }) => {
        // PR-B.2.5: route through the new-WS impl when active. The proposal_id
        // must be redeemed on the SAME WebSocket that issued it (probe-verified
        // 2026-05-12). socket-general's `buy` service uses `getAuthSocket()` —
        // the same singleton that `subscribeProposal` uses, so the constraint
        // is satisfied transparently.
        //
        // PR-RT.2: forward `parameters` + `subscribe` when present. The bot's
        // tradeOptionToBuy path (no-proposal direct-buy) sends:
        //   { buy: '1', subscribe: 1, price, parameters: {...} }
        // Previously these extras were dropped → server got incomplete payload
        // → bot stuck at "Buying contract" silently. Now fully forwarded.
        if (useNewWs() && _new_ws_buy) {
            return _new_ws_buy({ proposal_id, price, parameters, subscribe });
        }
        const payload = { buy: proposal_id, price };
        if (parameters) payload.parameters = parameters;
        if (subscribe) payload.subscribe = 1;
        return deriv_api.send(payload);
    };

    const sell = (contract_id, bid_price) => {
        // PR-B.2.5: same registry pattern as buy. Sell takes `contract_id`
        // (server-issued at buy time) and a min `bid_price` (0 = sell at any
        // price). Schema is byte-identical between legacy and new WS — only
        // the transport differs.
        if (useNewWs() && _new_ws_sell) {
            return _new_ws_sell(contract_id, bid_price);
        }
        return deriv_api.send({ sell: contract_id, price: bid_price });
    };

    const cashier = (action, parameters = {}) => deriv_api.send({ cashier: action, ...parameters });

    const cancelCryptoTransaction = transaction_id =>
        deriv_api.send({ cashier_withdrawal_cancel: 1, id: transaction_id });

    const newAccountVirtual = (verification_code, client_password, residence, device_data) =>
        deriv_api.send({
            new_account_virtual: 1,
            verification_code,
            client_password,
            residence,
            ...device_data,
        });

    const setAccountCurrency = (currency, passthrough) =>
        deriv_api.send({
            set_account_currency: currency,
            ...(passthrough && { passthrough }),
        });

    const newAccountReal = values =>
        deriv_api.send({
            new_account_real: 1,
            ...values,
        });

    const newAccountRealMaltaInvest = values => deriv_api.send({ new_account_maltainvest: 1, ...values });

    const mt5NewAccount = values =>
        deriv_api.send({
            mt5_new_account: 1,
            ...values,
        });

    const getFinancialAssessment = () =>
        deriv_api.send({
            get_financial_assessment: 1,
        });

    const setFinancialAndTradingAssessment = payload => deriv_api.send({ set_financial_assessment: 1, ...payload });

    const profitTable = (limit, offset, date_boundaries) =>
        deriv_api.send({ profit_table: 1, description: 1, limit, offset, ...date_boundaries });

    const statement = (limit, offset, other_properties) => {
        // PR-B.2.7: route through the new-WS impl when active. One-shot
        // request — same shape as legacy: `{statement: 1, limit, offset,
        // description, ...filter}` returns `{statement: {count,
        // transactions: [...]}, msg_type: 'statement'}`. Service uses
        // getAuthSocket() so it shares the singleton with everything else.
        if (useNewWs() && _new_ws_statement) {
            return _new_ws_statement({ limit, offset, description: 1, ...other_properties });
        }
        return deriv_api.send({ statement: 1, description: 1, limit, offset, ...other_properties });
    };

    const verifyEmail = (email, type, payload = {}) => deriv_api.send({ verify_email: email, type, ...payload });

    const tradingPlatformPasswordChange = payload =>
        deriv_api.send({
            trading_platform_password_change: 1,
            ...payload,
        });

    const tradingPlatformInvestorPasswordChange = payload =>
        deriv_api.send({
            trading_platform_investor_password_change: 1,
            ...payload,
        });

    const tradingPlatformInvestorPasswordReset = payload =>
        deriv_api.send({
            trading_platform_investor_password_reset: 1,
            ...payload,
        });

    const tradingPlatformPasswordReset = payload =>
        deriv_api.send({
            trading_platform_password_reset: 1,
            ...payload,
        });

    const tradingPlatformAvailableAccounts = platform =>
        deriv_api.send({
            trading_platform_available_accounts: 1,
            platform,
        });

    const paymentAgentList = (country, currency) =>
        deriv_api.send({ paymentagent_list: country, ...(currency && { currency }) });

    const allPaymentAgentList = country => deriv_api.send({ paymentagent_list: country });

    const paymentAgentDetails = (passthrough, req_id) =>
        deriv_api.send({ paymentagent_details: 1, passthrough, req_id });

    const paymentAgentWithdraw = ({ amount, currency, dry_run = 0, loginid, verification_code }) =>
        deriv_api.send({
            amount,
            currency,
            dry_run,
            paymentagent_loginid: loginid,
            paymentagent_withdraw: 1,
            verification_code,
        });

    const cryptoWithdraw = ({ address, amount, verification_code, dry_run = 0 }) =>
        deriv_api.send({
            cashier: 'withdraw',
            provider: 'crypto',
            type: 'api',
            address,
            amount,
            verification_code,
            dry_run,
        });

    const cryptoConfig = () =>
        deriv_api.send({
            crypto_config: 1,
        });

    const paymentAgentTransfer = ({ amount, currency, description, transfer_to, dry_run = 0 }) =>
        deriv_api.send({
            amount,
            currency,
            description,
            transfer_to,
            paymentagent_transfer: 1,
            dry_run,
        });

    const activeSymbols = (mode = 'brief') => {
        // PR-A.1.3: the trader loads its market dropdown via
        // WS.activeSymbols() (logged-out) and WS.authorized.activeSymbols()
        // (logged-in — same method behind the authorize-wait proxy). Route to
        // the new public WS (active_symbols needs no auth — probe-confirmed).
        if (useNewWs() && _new_ws_active_symbols) {
            return _new_ws_active_symbols(mode);
        }
        return deriv_api.activeSymbols(mode);
    };

    // PR-A.1.3: `contracts_for` own-method. Previously this resolved only via
    // the `delegateToObject` proxy fall-through to `deriv_api.contractsFor`,
    // which is undefined on new portal app_ids. The trader calls
    // `WS.contractsFor(symbol)` (contract-type.ts) + `WS.storage.contractsFor`
    // (trade-store loadActiveSymbols) to build the Trade Type dropdown.
    const contractsFor = symbol => {
        if (useNewWs() && _new_ws_contracts_for) {
            return _new_ws_contracts_for(symbol);
        }
        // Legacy path: delegate to the underlying deriv_api method if present.
        return deriv_api?.contractsFor
            ? deriv_api.contractsFor(symbol)
            : deriv_api.send({ contracts_for: symbol });
    };

    // PR-A.1.3: `trading_times` own-method (was proxy-only → undefined on new
    // portal). The trader calls `WS.tradingTimes(date)` for market hours.
    const tradingTimes = date => {
        if (useNewWs() && _new_ws_trading_times) {
            return _new_ws_trading_times(date);
        }
        return deriv_api?.tradingTimes
            ? deriv_api.tradingTimes(date)
            : deriv_api.send({ trading_times: date });
    };

    // PR-A.1.3: `time` own-method. The trader's wsSendRequest falls back to
    // WS.time() when ServerTime has no cached value.
    const time = () => {
        if (useNewWs() && _new_ws_time) {
            return _new_ws_time();
        }
        return deriv_api.send({ time: 1 });
    };

    const transferBetweenAccounts = (account_from, account_to, currency, amount) =>
        deriv_api.send({
            transfer_between_accounts: 1,
            accounts: 'all',
            ...(account_from && {
                account_from,
                account_to,
                currency,
                amount,
            }),
        });

    const forgetStream = id => {
        // PR-A.1.3: the trader unsubscribes tick streams via WS.forgetStream(id).
        if (useNewWs() && _new_ws_forget) {
            return _new_ws_forget(id);
        }
        return deriv_api.forget(id);
    };

    const tncApproval = () => deriv_api.send({ tnc_approval: '1' });

    // PR-CU.1: route contract update / history / cancel through the new auth WS
    // when active. Request shapes are byte-identical to the legacy payloads
    // (verified against the new-API docs), so the new-WS impls just carry them on
    // the getAuthSocket() transport. Legacy app_ids keep the deriv_api path.
    const contractUpdate = (contract_id, limit_order) => {
        if (useNewWs() && _new_ws_contract_update) {
            return _new_ws_contract_update(contract_id, limit_order);
        }
        return deriv_api.send({
            contract_update: 1,
            contract_id,
            limit_order,
        });
    };

    const contractUpdateHistory = contract_id => {
        if (useNewWs() && _new_ws_contract_update_history) {
            return _new_ws_contract_update_history(contract_id);
        }
        return deriv_api.send({
            contract_update_history: 1,
            contract_id,
        });
    };

    const cancelContract = contract_id => {
        if (useNewWs() && _new_ws_cancel_contract) {
            return _new_ws_cancel_contract(contract_id);
        }
        return deriv_api.send({ cancel: contract_id });
    };

    const fetchLoginHistory = limit =>
        deriv_api.send({
            login_history: 1,
            limit,
        });

    // subscribe method export for P2P use only
    // so that subscribe remains private
    const p2pSubscribe = (request, cb) => subscribe(request, cb);
    const accountStatistics = () => deriv_api.send({ account_statistics: 1 });

    const tradingServers = platform => deriv_api.send({ platform, trading_servers: 1 });

    const tradingPlatformAccountsList = platform =>
        deriv_api.send({
            trading_platform_accounts: 1,
            platform,
        });

    const tradingPlatformNewAccount = values =>
        deriv_api.send({
            trading_platform_new_account: 1,
            ...values,
        });

    const triggerMt5DryRun = ({ email }) =>
        deriv_api.send({
            account_type: 'financial',
            dry_run: 1,
            email,
            leverage: 100,
            mainPassword: 'Test1234',
            mt5_account_type: 'financial_stp',
            mt5_new_account: 1,
            name: 'test real labuan financial stp',
        });

    const getServiceToken = (platform, server) => {
        const temp_service = platform;

        return deriv_api.send({
            service_token: 1,
            service: temp_service,
            server,
        });
    };

    const changeEmail = api_request => deriv_api.send(api_request);

    // PR-A.1.3: `WS.storage` is the legacy DerivAPI cache layer
    // (`deriv_api.storage`) exposing cached one-shot helpers. The trader uses
    // `WS.storage.contractsFor(symbol)` (trade-store.loadActiveSymbols) and
    // `WS.storage.send(req)` (wsSendRequest fallback). On new portal app_ids
    // `deriv_api` is undefined, so the bare `delegateToObject` proxy returns
    // `undefined` for these → the trader crashes on `r.error`. This proxy
    // serves contractsFor / send from the new public WS when `useNewWs()` is
    // on, and otherwise falls through to the real `deriv_api.storage` exactly
    // as before. (Caching is a non-essential optimisation — the new public WS
    // is fast enough that an un-cached one-shot is fine.)
    const storageProxy = new Proxy(
        {},
        {
            get(_target, field) {
                if (useNewWs()) {
                    if (field === 'contractsFor' && _new_ws_contracts_for) {
                        return symbol => _new_ws_contracts_for(symbol);
                    }
                    if (field === 'send' && _new_ws_public_send) {
                        return request => _new_ws_public_send(request);
                    }
                }
                const storage = deriv_api?.storage;
                if (!storage) return undefined;
                const value = storage[field];
                return typeof value === 'function' ? value.bind(storage) : value;
            },
        }
    );

    return {
        init,
        openNewConnection,
        forgetStream,
        wait,
        availability,
        hasReadyState,
        isSiteDown,
        isSiteUpdating,
        clear: () => {
            // do nothing.
        },
        sendBuffered: () => {
            // do nothing.
        },
        getSocket: () => binary_socket,
        get: () => deriv_api,
        getAvailability: () => availability,
        setOnDisconnect: onDisconnect => {
            config.onDisconnect = onDisconnect;
        },
        setOnReconnect: onReconnect => {
            config.onReconnect = onReconnect;
        },
        removeOnReconnect: () => {
            delete config.onReconnect;
        },
        removeOnDisconnect: () => {
            delete config.onDisconnect;
        },
        cache: delegateToObject({}, () => deriv_api?.cache),
        // PR-A.1.3: storage proxy that routes contractsFor / send to the new
        // public WS on new portal app_ids (see storageProxy docblock above).
        storage: storageProxy,
        blockRequest,
        buy,
        buyAndSubscribe,
        sell,
        cashier,
        cancelCryptoTransaction,
        cancelContract,
        close,
        cryptoWithdraw,
        cryptoConfig,
        contractUpdate,
        contractUpdateHistory,
        getFinancialAssessment,
        setFinancialAndTradingAssessment,
        mt5NewAccount,
        newAccountVirtual,
        newAccountReal,
        newAccountRealMaltaInvest,
        p2pSubscribe,
        profitTable,
        statement,
        verifyEmail,
        getTicksHistory,
        tradingPlatformPasswordChange,
        tradingPlatformPasswordReset,
        tradingPlatformAvailableAccounts,
        tradingPlatformInvestorPasswordChange,
        tradingPlatformInvestorPasswordReset,
        activeSymbols,
        paymentAgentList,
        allPaymentAgentList,
        paymentAgentDetails,
        paymentAgentWithdraw,
        paymentAgentTransfer,
        setAccountCurrency,
        balanceAll,
        setAvailability,
        subscribeBalanceAll,
        subscribeBalanceActiveAccount,
        subscribeProposal,
        // PR-B.2.4: registry setter + explicit forgetAll override so that
        // WS.forgetAll('proposal') reaches the new-WS proposal teardown
        // when useNewWs() is true. See setProposalNewWsImpl docblock above.
        setProposalNewWsImpl,
        // PR-A.1.3: public-data registry setter (active_symbols / contracts_for
        // / ticks / ticks_history / trading_times / time / wait) so the trader
        // at '/dtrader' resolves its public-data calls on the new public WS.
        setPublicDataNewWsImpl,
        // PR-A.1.3: own-methods that previously resolved only via the
        // delegateToObject proxy → undefined on new portal app_ids.
        contractsFor,
        tradingTimes,
        time,
        forgetAll,
        subscribeProposalOpenContract,
        subscribeTicks,
        subscribeTicksHistory,
        subscribeTransaction,
        subscribeWebsiteStatus,
        tncApproval,
        transferBetweenAccounts,
        fetchLoginHistory,
        closeAndOpenNewConnection,
        accountStatistics,
        tradingServers,
        tradingPlatformAccountsList,
        tradingPlatformNewAccount,
        triggerMt5DryRun,
        getServiceToken,
        changeEmail,
    };
})();

function delegateToObject(base_obj, extending_obj_getter) {
    return new Proxy(base_obj, {
        get(target, field) {
            if (target[field]) return target[field];

            const extending_obj =
                typeof extending_obj_getter === 'function' ? extending_obj_getter() : extending_obj_getter;

            if (!extending_obj) return undefined;

            const value = extending_obj[field];
            if (value) {
                if (typeof value === 'function') {
                    return value.bind(extending_obj);
                }
                return value;
            }

            return undefined;
        },
    });
}

const proxied_socket_base = delegateToObject(BinarySocketBase, () => BinarySocketBase.get());

// Promise that never resolves — used when the legacy WS is intentionally gated
// (see `isLegacyApiAppId` in `openNewConnection`). Pre-gate, `wait('authorize')`
// would simply not resolve until the WS authorize completed; downstream call
// sites awaited it forever. Post-gate, `wait('authorize')` returns `undefined`
// (because `deriv_api` is never initialised), and `.then()` would throw
// `TypeError: Cannot read properties of undefined (reading 'then')`, crashing
// the app into the error boundary. Returning this never-resolving promise
// preserves the pre-gate semantic: callers hang silently rather than crash.
// Once the new WS layer (PR-B) lands, code paths should migrate off the
// `BinarySocketBase.authorized` proxy entirely.
const NEVER_RESOLVING_PROMISE = new Promise(() => {});

const proxyForAuthorize = obj =>
    new Proxy(obj, {
        get(target, field) {
            if (target[field] && typeof target[field] !== 'function') {
                return proxyForAuthorize(target[field]);
            }
            return (...args) => {
                const wait_promise = BinarySocketBase?.wait('authorize');
                if (!wait_promise) return NEVER_RESOLVING_PROMISE;
                return wait_promise.then(() => target[field](...args));
            };
        },
    });

BinarySocketBase.authorized = proxyForAuthorize(proxied_socket_base);

module.exports = proxied_socket_base;
